const HOUR_MS = 60 * 60 * 1000;
/** Same threshold as the backend `staleShopsCount`. */
const STATS_STALE_AFTER_MS = 2 * HOUR_MS;
/** Under this share of the Shopify products loaded, the catalog is incomplete. */
const CATALOG_MIN_LOADED_SHARE = 0.9;
/** Smaller catalogs are left out: a few products off is noise. */
const CATALOG_MIN_PRODUCTS = 50;

export type WatchShop = {
  id: string;
  name: string;
  isTestShop?: boolean | null;
  isActive?: boolean | null;
  clientStatus?: string | null;
  returnRequestsLast30Days: number;
  returnRequestsLast7Days: number;
  lastActivityAt?: number | null;
  statsComputedAt?: number | null;
};

type WatchCatalog = {
  shopId: string;
  catalogProductCount?: number | null;
  catalogProductCountProbedAt?: number | null;
  loadedCatalogProductCount?: number | null;
};

export type WatchItem =
  | {
      reason: "catalog-gap";
      shopId: string;
      shopName: string;
      loaded: number;
      expected: number;
      probedAt: number | null;
    }
  | {
      reason: "activity-drop";
      shopId: string;
      shopName: string;
      returns30d: number;
      lastActivityAt: number | null;
    }
  | {
      reason: "inactive-but-used";
      shopId: string;
      shopName: string;
      returns7d: number;
    }
  | {
      reason: "stale-stats";
      shopId: string;
      shopName: string;
      statsComputedAt: number | null;
    };

/**
 * Shops that need a look, one item per reason, test shops left out. Each
 * item carries the figures that justify it, so the panel can say why.
 */
export function buildWatchList(
  shops: WatchShop[],
  catalogs: WatchCatalog[],
  now: number,
): WatchItem[] {
  const catalogByShop = new Map(catalogs.map((item) => [item.shopId, item]));
  const items: WatchItem[] = [];

  for (const shop of shops) {
    if (shop.isTestShop) continue;
    const base = { shopId: shop.id, shopName: shop.name };

    const catalog = catalogByShop.get(shop.id);
    const expected = catalog?.catalogProductCount ?? null;
    const loaded = catalog?.loadedCatalogProductCount ?? null;
    if (
      expected !== null &&
      loaded !== null &&
      expected >= CATALOG_MIN_PRODUCTS &&
      loaded < expected * CATALOG_MIN_LOADED_SHARE
    ) {
      items.push({
        ...base,
        reason: "catalog-gap",
        loaded,
        expected,
        probedAt: catalog?.catalogProductCountProbedAt ?? null,
      });
    }

    if (
      shop.isActive !== false &&
      shop.clientStatus === "active" &&
      shop.returnRequestsLast30Days >= 10 &&
      shop.returnRequestsLast7Days === 0
    ) {
      items.push({
        ...base,
        reason: "activity-drop",
        returns30d: shop.returnRequestsLast30Days,
        lastActivityAt: shop.lastActivityAt ?? null,
      });
    }

    if (shop.isActive === false && shop.returnRequestsLast7Days > 0) {
      items.push({
        ...base,
        reason: "inactive-but-used",
        returns7d: shop.returnRequestsLast7Days,
      });
    }

    const computedAt = shop.statsComputedAt ?? null;
    if (
      shop.isActive !== false &&
      (computedAt === null || computedAt < now - STATS_STALE_AFTER_MS)
    ) {
      items.push({
        ...base,
        reason: "stale-stats",
        statsComputedAt: computedAt,
      });
    }
  }

  return items;
}
